import React, { useState, useRef } from 'react';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs, { Dayjs } from 'dayjs';
import {
  BASIC_AWAKE_TIME,
  BASIC_SLEEP_TIME,
  CustomTimeSelector,
  OnboardingWrapper,
} from '../../../components';
import { useOnboarding } from '../../../data-access';
import { ReactComponent as EditIcon } from '../../../assets/onboarding/edit_icon.svg';
import './ManualSleepInputPage.scss';
import { useBreakpoints } from '../../../utils';
import { Stack } from '@mui/material';
import { useEditSleep } from '../../../hooks';

export const ManualSleepInputPage: React.FC = () => {
  const { isMobile } = useBreakpoints();
  const { currentOnboardingStep, goToNextStep, goToPreviousStep, updateOnboardingStatus } =
    useOnboarding();
  const { handleEditSleepData, isEditingData, canSubmitSleepEdit } = useEditSleep({
    date: dayjs(),
    skipAutoSuggestion: true,
  });

  const [sleepTime, setSleepTime] = useState<Dayjs>(
    dayjs().set('hour', BASIC_SLEEP_TIME).set('minute', 0).set('second', 0)
  );
  const [wakeTime, setWakeTime] = useState<Dayjs>(
    dayjs().set('hour', BASIC_AWAKE_TIME).set('minute', 0).set('second', 0)
  );

  const sleepTimeRef = useRef<HTMLDivElement>(null);
  const wakeTimeRef = useRef<HTMLDivElement>(null);
  const [openSleepTimePicker, setOpenSleepTimePicker] = useState(false);
  const [openWakeTimePicker, setOpenWakeTimePicker] = useState(false);

  const handleSleepTimeEdit = () => {
    setOpenSleepTimePicker(true);
  };

  const handleWakeTimeEdit = () => {
    setOpenWakeTimePicker(true);
  };

  const handleContinue = async () => {
    const todayWakeTime = dayjs()
      .set('hour', wakeTime.hour())
      .set('minute', wakeTime.minute())
      .set('second', 0)
      .toDate();

    // If sleepTime is after wakeTime, set sleepTime to the previous day
    let adjustedSleepTime = sleepTime;
    if (dayjs(sleepTime).isAfter(todayWakeTime)) {
      adjustedSleepTime = dayjs(sleepTime).subtract(1, 'day');
    }

    if (!canSubmitSleepEdit) {
      console.error('Unable to update sleep details at this time.');
      return;
    }

    await handleEditSleepData({
      sleepTime: dayjs(adjustedSleepTime).format('YYYY-MM-DDTHH:mm:ss'),
      wakeTime: dayjs(todayWakeTime).format('YYYY-MM-DDTHH:mm:ss'),
    });
    goToNextStep();
  };

  return (
    <OnboardingWrapper
      currentOnboardingStep={currentOnboardingStep}
      onBack={goToPreviousStep}
      title="Your energy starts with sleep"
      subTitle={`When and how you sleep shapes your focus, mood, and motivation the next day.\nEnter your usual times — your best guess.`}
      contentContainerStyle={{
        display: 'flex',
        flexDirection: 'column',
        width: isMobile ? '100%' : '600px',
      }}
      buttons={[
        {
          text: 'Continue',
          onPress: handleContinue,
          variant: 'contained',
          loading: isEditingData,
          disabled: isEditingData || !canSubmitSleepEdit,
        },
      ]}
      buttonContainerStyle={{ gap: 24 }}
    >
      <div className="manual-sleep-input-page__fields">
        <div className="manual-sleep-input-page__fields__row">
          Sleep time:
          <Stack direction="row" alignItems="center" gap={1}>
            <div ref={sleepTimeRef}>
              <CustomTimeSelector
                value={sleepTime}
                onChange={(newValue) => newValue && setSleepTime(newValue)}
                autocompleteClassName="manual-sleep-input-page__fields__row__timefield"
                amPm={true}
                className="manual-sleep-input-page__fields__row__timefield-container"
                open={openSleepTimePicker}
                setOpen={setOpenSleepTimePicker}
                paperClassName="manual-sleep-input-page__fields__row__paper"
              />
            </div>
            <EditIcon onClick={handleSleepTimeEdit} style={{ cursor: 'pointer' }} />
          </Stack>
        </div>
        <div className="manual-sleep-input-page__fields__row">
          Wake time:
          <Stack direction="row" alignItems="center" gap={1}>
            <div ref={wakeTimeRef}>
              <CustomTimeSelector
                value={wakeTime}
                onChange={(newValue) => newValue && setWakeTime(newValue)}
                amPm={true}
                autocompleteClassName="manual-sleep-input-page__fields__row__timefield"
                className="manual-sleep-input-page__fields__row__timefield-container"
                open={openWakeTimePicker}
                setOpen={setOpenWakeTimePicker}
                paperClassName="manual-sleep-input-page__fields__row__paper"
              />
            </div>
            <EditIcon onClick={handleWakeTimeEdit} style={{ cursor: 'pointer' }} />
          </Stack>
        </div>
      </div>
    </OnboardingWrapper>
  );
};
